import { useState, useEffect } from "react";
import { useApp } from "@/App";
import { Menu, X, MapPin, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Navigation() {
  const { selectedLocation, setSelectedLocation } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Rezervace", href: "#booking" },
    { label: "Služby", href: "#services" },
    { label: "Ceník", href: "#pricing" },
    { label: "O nás", href: "#about" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const locationName = selectedLocation === "cestlice" ? "Čestlice" : "Butovice";
  const locationPhone = selectedLocation === "cestlice" ? "+420 730 595 595" : "+420 732 327 777";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "glass-strong py-3" : "bg-transparent py-5"
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedLocation(null)}
              className="text-white hover:text-[#39FF14] transition-colors duration-300"
              data-testid="nav-logo"
            >
              <span className="text-xl font-black tracking-tighter uppercase">
                CAR <span className="text-[#39FF14]">SPA</span>
              </span>
            </button>
            <div className="hidden md:flex items-center gap-1 text-zinc-500 text-xs">
              <MapPin className="w-3 h-3" />
              <span className="tracking-wider uppercase">{locationName}</span>
            </div>
            <a
              href={`tel:${locationPhone.replace(/\s/g, "")}`}
              className="hidden md:flex items-center gap-1.5 text-zinc-400 text-xs hover:text-[#39FF14] transition-colors duration-300"
              data-testid="nav-phone"
            >
              <Phone className="w-3 h-3" />
              <span>{locationPhone}</span>
            </a>
          </div>

          {/* Mobile phone - visible between logo and burger */}
          <a
            href={`tel:${locationPhone.replace(/\s/g, "")}`}
            className="md:hidden flex items-center gap-1.5 text-[#39FF14] text-xs font-bold"
            data-testid="nav-phone-mobile"
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="text-[11px]">{locationPhone}</span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollTo(item.href)}
                className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 tracking-wide uppercase font-medium"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#booking")}
              className="btn-skew bg-[#39FF14] text-black px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300"
              data-testid="nav-booking-btn"
            >
              <span>Rezervovat</span>
            </button>
          </div>

          {/* Mobile burger */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
          >
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollTo(item.href)}
                className="text-2xl text-white font-bold uppercase tracking-wider hover:text-[#39FF14] transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#booking")}
              className="btn-skew bg-[#39FF14] text-black px-8 py-3 text-sm font-bold uppercase tracking-wider mt-4"
            >
              <span>Rezervovat</span>
            </button>
            <a
              href={`tel:${locationPhone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-zinc-400 text-sm mt-4 hover:text-[#39FF14] transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              {locationPhone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
